import React, { useEffect } from 'react';
import {
  Search,
  MessageSquare,
  FileText,
  ChartNoAxesColumnIncreasing,
  Clock,
  FolderOpen,
  TrendingUp,
  Loader2,
} from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchUserStatistics } from '../../redux/slices/userStatisticsSlice';
import { formatRelativeTime } from '../../helpers/dateHelper';
import { fetchMeThunk } from '../../redux/slices/authSlice';
import { motion, AnimatePresence } from 'framer-motion';

const OverviewTab = ({ isDarkMode }) => {
  const { user } = useSelector((state) => state.auth);
  const { stats, loading } = useSelector((state) => state.userStatistics);
  const order = user?.Orders || null;
  const isPending = order?.status === 'pending';

  // const pendingOrder = orders.find(
  //   (o) => o.status === 'pending' && Number(o.amount) > 0
  // );

  const dispatch = useDispatch();
  const uid = user?.id;
  const name = user?.name || user?.email || 'Guest';
  const activityIconMap = {
    research: Search,
    chat: MessageSquare,
    contract_analysis: FileText,
  };
  const getActivityIcon = (activity) => {
    const type = activity?.activity_type;

    return activityIconMap[type] || MessageSquare; // fallback
  };

  useEffect(() => {
    if (uid) dispatch(fetchUserStatistics(uid));
  }, [uid, dispatch]);
  useEffect(() => {
    if (!isPending) return;

    const interval = setInterval(() => {
      dispatch(fetchMeThunk());
    }, 10000);

    return () => clearInterval(interval);
  }, [isPending, dispatch]);
  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-40 text-gray-500">
        <Loader2 className="animate-spin w-5 h-5 mr-2" />
        Loading overview...
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="flex items-center justify-center h-40 text-gray-500">
        No statistics available
      </div>
    );
  }

  const { usage_month_to_date, raw_features } = stats;

  return (
    <>
      <div
        className={`
        border rounded-lg p-4 shadow-sm w-full
        ${
          isDarkMode
            ? 'bg-gray-700 border-gray-600 text-white'
            : 'bg-white border-gray-200 text-gray-900'
        }
      `}
      >
        <AnimatePresence mode="wait">
          {isPending ? (
            <motion.div
              key="pending"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.35, ease: 'easeOut' }}
              className="flex flex-col items-center justify-center py-20 text-center"
            >
              <Loader2 className="w-8 h-8 text-brand mb-4 animate-spin" />

              <h3 className="text-lg font-semibold mb-2">
                Setting up your subscription
              </h3>
              <p className="text-sm text-gray-500 max-w-md leading-relaxed">
                Your payment is confirmed.
                <br />
                We’re activating your subscription now — this usually takes a
                moment.
              </p>
              <p className="mt-3 text-xs text-gray-400">
                This page will update automatically.
              </p>
            </motion.div>
          ) : (
            <>
              <motion.div
                key="overview"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35, ease: 'easeOut' }}
              >
                {/* Greeting */}
                <div className="mb-6">
                  <h2
                    className={`text-lg font-bold ${
                      isDarkMode ? 'text-white' : 'text-gray-800'
                    }`}
                  >
                    Hello {name}
                  </h2>
                  <p
                    className={`text-sm ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-500'
                    }`}
                  >
                    Here’s an overview of your legal workspace
                  </p>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  {[
                    {
                      icon: <MessageSquare className="w-5 h-5" />,
                      title: 'Queries this Month',
                      value:
                        raw_features?.chatbot_queries?.value -
                          usage_month_to_date?.chatbot_queries?.value || 0,
                    },
                    {
                      icon: <ChartNoAxesColumnIncreasing className="w-5 h-5" />,
                      title: 'Document Analyzed',
                      value:
                        raw_features?.document_analysis?.value -
                          usage_month_to_date?.document_analysis?.value || 0,
                    },
                    {
                      icon: <FolderOpen className="w-5 h-5" />,
                      title: 'Available Spaces',
                      value: usage_month_to_date?.organized_spaces?.value || 0,
                    },
                  ].map((item, idx) => (
                    <div
                      key={idx}
                      className={`p-4 rounded-lg ${
                        isDarkMode
                          ? 'bg-gray-600 text-white'
                          : 'bg-[#F9FAFB] text-gray-800'
                      }`}
                    >
                      <div className="flex items-center space-x-4">
                        <div className="bg-brand text-white p-3 rounded-lg">
                          {item.icon}
                        </div>
                        <div>
                          <p
                            className={`text-xs ${
                              isDarkMode ? 'text-gray-300' : 'text-gray-600'
                            }`}
                          >
                            {item.title}
                          </p>
                          <p className="text-xl font-bold">{item.value}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Recent Activity + Usage */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Recent Activity */}
                  <div
                    className={`p-3 rounded-lg shadow ${
                      isDarkMode
                        ? 'bg-gray-600 text-white'
                        : 'bg-white text-gray-800'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-base font-semibold">
                        Recent Activity
                      </h3>
                      <Clock className="w-4 h-4 text-gray-400" />
                    </div>
                    {Array.isArray(stats?.last_activity) &&
                    stats.last_activity.length > 0 ? (
                      <div className="space-y-4">
                        {stats.last_activity
                          .filter(
                            (a) =>
                              a &&
                              (!a.context?.role || a.context?.role === 'user')
                          )
                          .sort(
                            (a, b) =>
                              new Date(b?.timestamp || 0) -
                              new Date(a?.timestamp || 0)
                          )
                          .slice(0, 3)
                          .map((activity, index) => {
                            if (!activity) return null;

                            const Icon = getActivityIcon(activity);

                            return (
                              <div
                                key={index}
                                className="flex items-start space-x-4"
                              >
                                <div
                                  className={`p-3 rounded-lg flex items-center justify-center ${
                                    isDarkMode
                                      ? 'bg-indigo-900/30 text-indigo-300'
                                      : 'bg-indigo-100 text-brand'
                                  }`}
                                >
                                  <Icon className="w-5 h-5" />
                                </div>

                                <div className="flex-1">
                                  <p className="font-bold text-sm mb-1">
                                    {activity.description || 'No description'}
                                  </p>
                                  <p className="text-xs text-gray-400">
                                    {formatRelativeTime(activity.timestamp)}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    ) : (
                      <p className="text-sm italic text-center text-gray-400">
                        No recent activity.
                      </p>
                    )}
                  </div>

                  {/* Usage Statistics */}
                  <div
                    className={`p-3 rounded-lg shadow ${
                      isDarkMode
                        ? 'bg-gray-600 text-white'
                        : 'bg-white text-gray-800'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-base font-semibold">
                        Usage Statistics
                      </h3>
                      <TrendingUp className="w-4 h-4 text-gray-400" />
                    </div>

                    <div className="space-y-6">
                      {[
                        {
                          label: 'AI Chat Queries',
                          value:
                            raw_features.chatbot_queries?.value -
                              usage_month_to_date.chatbot_queries?.value || 0,
                          max: raw_features.chatbot_queries?.value || 0,
                        },
                        {
                          label: 'Professional Research',
                          value:
                            raw_features.professional_research?.value -
                              usage_month_to_date.professional_research
                                ?.value || 0,
                          max: raw_features.professional_research?.value || 0,
                        },
                        {
                          label: 'Document Analysis',
                          value:
                            raw_features.document_analysis?.value -
                              usage_month_to_date.document_analysis?.value || 0,
                          max: raw_features.document_analysis?.value || 0,
                        },
                      ].map((stat, idx) => (
                        <div key={idx}>
                          {/* LABEL & VALUE */}
                          <div
                            className={`flex justify-between mb-2 text-xs ${
                              isDarkMode ? 'text-slate-300' : 'text-black'
                            }`}
                          >
                            <span className="font-bold">{stat.label}</span>
                            <span>
                              {stat.value}/{stat.max}
                            </span>
                          </div>

                          {/* PROGRESS BAR BACKGROUND */}
                          <div
                            className={`w-full rounded-full h-2 ${
                              isDarkMode ? 'bg-slate-700' : 'bg-gray-200'
                            }`}
                          >
                            {/* PROGRESS BAR FILL */}
                            <div
                              className={`h-2 rounded-full ${
                                isDarkMode ? 'bg-indigo-400' : 'bg-brand'
                              }`}
                              style={{
                                width: `${Math.min(
                                  (stat.value / stat.max) * 100,
                                  100
                                )}%`,
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export default OverviewTab;
